// §15 — system sounds.
//
// No audio files anywhere (D-043): a skin supplies a `SoundRecipe` per `SoundId` and this
// service builds the oscillator graph. The `AudioContext` is injected so the whole thing is
// testable without a browser, and nothing plays before a user gesture — the autoplay policy
// would reject it and a rejected promise is not an error worth surfacing.
import type { SoundId, SoundRecipe, SoundTone } from '@hangar/shared';
import { serviceToken, type Kernel } from '../registry';

// ── The slice of WebAudio this service uses. The real `AudioContext` satisfies it
//    structurally, so production passes the genuine article and tests pass a recorder.

export interface AudioParamLike {
  setValueAtTime(value: number, at: number): unknown;
  linearRampToValueAtTime(value: number, at: number): unknown;
  exponentialRampToValueAtTime(value: number, at: number): unknown;
}

export interface AudioNodeLike {
  connect(destination: AudioNodeLike): unknown;
  disconnect(): unknown;
}

export interface OscillatorLike extends AudioNodeLike {
  type: OscillatorType;
  frequency: AudioParamLike;
  start(at: number): unknown;
  stop(at: number): unknown;
  onended: unknown;
}

export interface GainLike extends AudioNodeLike {
  gain: AudioParamLike;
}

export interface AudioContextLike {
  readonly currentTime: number;
  readonly destination: AudioNodeLike;
  readonly state: string;
  createOscillator(): OscillatorLike;
  createGain(): GainLike;
  resume(): Promise<void>;
}

export interface SoundPreferences {
  muted: boolean;
  disabled: readonly SoundId[];
}

export interface SoundOptions {
  /** Constructed lazily, on the first gesture — an `AudioContext` made before one is suspended. */
  createContext?: () => AudioContextLike | null;
  /** Read at each `play()`; wired to the settings service by the composition root. */
  preferences?: () => SoundPreferences;
}

export interface SoundService {
  /** The active skin's recipe table (§6.1). Replaced on every skin change. */
  setRecipes(recipes: Partial<Record<SoundId, SoundRecipe>>): void;
  /** Record that the user has interacted, unlocking audio. Idempotent. */
  noteUserGesture(): void;
  /** True once a gesture has been seen. */
  unlocked(): boolean;
  /** Fire and forget. Never throws, never blocks the action that triggered it (§15). */
  play(id: SoundId): void;
}

const DEFAULT_PREFERENCES: SoundPreferences = { muted: false, disabled: [] };

/** Seconds. Guards against a recipe with a zero or negative duration. */
const MIN_TONE_DURATION = 0.005;
/** `exponentialRampToValueAtTime` rejects zero, so envelopes decay to this instead. */
const SILENCE = 0.0001;

function browserContextFactory(): AudioContextLike | null {
  const Ctor = globalThis.AudioContext;
  if (typeof Ctor !== 'function') return null;
  return new Ctor() as unknown as AudioContextLike;
}

export function scheduleTone(ctx: AudioContextLike, tone: SoundTone, startAt: number): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const t0 = startAt + Math.max(0, tone.at);
  const duration = Math.max(MIN_TONE_DURATION, tone.duration);
  const t1 = t0 + duration;

  osc.type = tone.wave;
  osc.frequency.setValueAtTime(tone.freq, t0);
  if (tone.endFreq !== undefined && tone.endFreq > 0) {
    osc.frequency.exponentialRampToValueAtTime(tone.endFreq, t1);
  }

  const attack = Math.min(tone.attack ?? 0.005, duration / 2);
  const release = Math.min(tone.release ?? 0.02, duration / 2);
  gain.gain.setValueAtTime(SILENCE, t0);
  gain.gain.linearRampToValueAtTime(tone.gain, t0 + attack);
  gain.gain.setValueAtTime(tone.gain, t1 - release);
  gain.gain.exponentialRampToValueAtTime(SILENCE, t1);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t1);
}

export function createSoundService(options: SoundOptions = {}): SoundService {
  const createContext = options.createContext ?? browserContextFactory;
  const preferences = options.preferences ?? (() => DEFAULT_PREFERENCES);

  let recipes: Partial<Record<SoundId, SoundRecipe>> = {};
  let gestureSeen = false;
  let ctx: AudioContextLike | null = null;
  let contextTried = false;

  const context = (): AudioContextLike | null => {
    if (!contextTried) {
      contextTried = true;
      ctx = createContext();
    }
    return ctx;
  };

  return {
    setRecipes(next) {
      recipes = next;
    },

    noteUserGesture() {
      if (gestureSeen) return;
      gestureSeen = true;
      const c = context();
      if (c && c.state === 'suspended') void c.resume().catch(() => undefined);
    },

    unlocked() {
      return gestureSeen;
    },

    play(id) {
      if (!gestureSeen) return;
      const prefs = preferences();
      if (prefs.muted || prefs.disabled.includes(id)) return;

      const recipe = recipes[id];
      if (!recipe || recipe.tones.length === 0) return;

      const c = context();
      if (!c) return;

      const startAt = c.currentTime;
      for (const tone of recipe.tones) {
        try {
          scheduleTone(c, tone, startAt);
        } catch {
          // A sound never blocks the action that triggered it (§15).
          return;
        }
      }
    },
  };
}

export const SOUND_SERVICE = serviceToken<SoundService>('sound');

export function provideSound(kernel: Kernel, options: SoundOptions = {}): void {
  kernel.provide(SOUND_SERVICE, () => createSoundService(options));
}
